import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  GetUserCountsRequest,
  PushNotificationsService,
  PushNotificationTarget,
  PushNotificationTargetType,
  SendPushNotificationRequest,
} from '@services/push-notifications.service';
import { ToastService } from '@services/toast.service';
import { WaterBasic } from '@services/waters.service';
import { assertUnreachable } from '@utils/assert-unreachable';
import { toDateOnly } from '@utils/date-only';
import { firstValueFrom } from 'rxjs';
import { WaterSelectorDialogComponent } from '../water-services/water-services-dialog/water-selector-dialog.component';

type Locale = 'Nl' | 'En' | 'De' | 'It' | 'Fr';

const SOURCE_LOCALE: Locale = 'Nl';
const TARGET_LOCALES: Locale[] = ['En', 'De', 'It', 'Fr'];

@Component({
  selector: 'app-push-notifications',
  templateUrl: './push-notifications.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTabsModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PushNotificationsComponent {
  private readonly pushNotificationsService = inject(PushNotificationsService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly sending = signal(false);
  readonly lastResult = signal<{ success: boolean; message: string } | null>(null);
  readonly activeLocale = signal<Locale>('Nl');
  readonly currentStage = signal<1 | 2>(1); // Stage 1: Target selection, Stage 2: Language & content
  readonly loadingUserCounts = signal(false);
  readonly userCounts = signal<Record<string, number>>({});
  readonly pushTokenCounts = signal<Record<string, number>>({});
  // Informational only: per push-language, how that language's users distribute across the site
  // locale(s) of their arriving reservations. Empty unless a single ArrivalDate target was used.
  readonly siteCountsByLanguage = signal<Record<string, Record<string, number>>>({});

  // Whether any per-language site distribution is available (drives the info message card).
  readonly hasSiteInfo = computed(() => Object.keys(this.siteCountsByLanguage()).length > 0);

  readonly TargetType = PushNotificationTargetType;

  readonly locales: { code: Locale; label: string; enabled: boolean }[] = [
    { code: 'Nl', label: 'NL', enabled: false },
    { code: 'En', label: 'EN', enabled: false },
    { code: 'De', label: 'DE', enabled: false },
    { code: 'It', label: 'IT', enabled: false },
    { code: 'Fr', label: 'FR', enabled: false },
  ];

  readonly activeLocaleIndex = computed(() => {
    return this.locales.findIndex((l) => l.code === this.activeLocale());
  });

  readonly form = new FormGroup<{
    titleNl: FormControl<string>;
    titleEn: FormControl<string>;
    titleDe: FormControl<string>;
    titleIt: FormControl<string>;
    titleFr: FormControl<string>;
    bodyNl: FormControl<string>;
    bodyEn: FormControl<string>;
    bodyDe: FormControl<string>;
    bodyIt: FormControl<string>;
    bodyFr: FormControl<string>;
    targets: FormArray<
      FormGroup<{
        type: FormControl<PushNotificationTargetType>;
        reservationNumber: FormControl<string>;
        waterId: FormControl<number | null>;
        waterTitle: FormControl<string>;
        arrivalDateFrom: FormControl<Date | null>;
        arrivalDateTo: FormControl<Date | null>;
        userEmail: FormControl<string>;
      }>
    >;
  }>({
    // Title and body fields start without required validators
    titleNl: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(100)] }),
    titleEn: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(100)] }),
    titleDe: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(100)] }),
    titleIt: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(100)] }),
    titleFr: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(100)] }),
    bodyNl: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
    bodyEn: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
    bodyDe: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
    bodyIt: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
    bodyFr: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
    targets: new FormArray([this.createTargetGroup()]),
  });

  private createTargetGroup(): FormGroup<{
    type: FormControl<PushNotificationTargetType>;
    reservationNumber: FormControl<string>;
    waterId: FormControl<number | null>;
    waterTitle: FormControl<string>;
    arrivalDateFrom: FormControl<Date | null>;
    arrivalDateTo: FormControl<Date | null>;
    userEmail: FormControl<string>;
  }> {
    return new FormGroup({
      type: new FormControl<PushNotificationTargetType>(PushNotificationTargetType.Global, {
        nonNullable: true,
        validators: Validators.required,
      }),
      reservationNumber: new FormControl('', { nonNullable: true }),
      waterId: new FormControl<number | null>(null),
      waterTitle: new FormControl('', { nonNullable: true }),
      arrivalDateFrom: new FormControl<Date | null>(null),
      arrivalDateTo: new FormControl<Date | null>(null),
      userEmail: new FormControl('', { nonNullable: true }),
    });
  }

  addTarget(): void {
    this.form.controls.targets.push(this.createTargetGroup());
  }

  removeTarget(index: number): void {
    if (this.form.controls.targets.length > 1) {
      this.form.controls.targets.removeAt(index);
    }
  }

  async selectWater(targetIndex: number): Promise<void> {
    const dialogRef = this.dialog.open<WaterSelectorDialogComponent, object, WaterBasic>(WaterSelectorDialogComponent, {
      width: '600px',
    });

    const water = await firstValueFrom(dialogRef.afterClosed());
    if (water) {
      const targetGroup = this.form.controls.targets.at(targetIndex);
      targetGroup.patchValue({
        waterId: water.id,
        waterTitle: water.title,
      });
      this.cdr.markForCheck();
    }
  }

  clearWater(targetIndex: number): void {
    const targetGroup = this.form.controls.targets.at(targetIndex);
    targetGroup.patchValue({
      waterId: null,
      waterTitle: '',
    });
    this.cdr.markForCheck();
  }

  async proceedToStage2(): Promise<void> {
    // Validate targets first
    const validationError = this.validateTargets();
    if (validationError) {
      this.toastService.showError(validationError);
      return;
    }

    this.loadingUserCounts.set(true);

    try {
      const formValue = this.form.getRawValue();
      const targets: PushNotificationTarget[] = formValue.targets.map((target) => {
        const pushTarget: PushNotificationTarget = {
          type: target.type,
        };

        switch (target.type) {
          case PushNotificationTargetType.Reservation:
            pushTarget.reservationNumber = target.reservationNumber;
            break;
          case PushNotificationTargetType.Water:
            pushTarget.waterId = target.waterId ?? undefined;
            break;
          case PushNotificationTargetType.ArrivalDate:
            pushTarget.arrivalDateFrom = target.arrivalDateFrom ? this.formatDate(target.arrivalDateFrom) : undefined;
            pushTarget.arrivalDateTo = target.arrivalDateTo ? this.formatDate(target.arrivalDateTo) : undefined;
            break;
          case PushNotificationTargetType.User:
            pushTarget.userEmail = target.userEmail;
            break;
          case PushNotificationTargetType.Global:
            // No fields
            break;
          default:
            assertUnreachable(target.type);
        }

        return pushTarget;
      });

      const request: GetUserCountsRequest = { targets };
      const response = await firstValueFrom(this.pushNotificationsService.getUserCounts(request));

      this.userCounts.set(response.userCounts);
      this.pushTokenCounts.set(response.pushTokenCounts);
      this.siteCountsByLanguage.set(response.siteCountsByLanguage ?? {});

      // Auto-enable languages with at least 1 user
      this.locales.forEach((locale) => {
        const count = response.userCounts[locale.code.toLowerCase()] || 0;
        locale.enabled = count > 0;

        // Update validators based on enabled state
        this.updateLocaleValidators(locale.code, locale.enabled);
      });

      this.currentStage.set(2);
      this.cdr.markForCheck();
    } catch (error: unknown) {
      console.error('Get user counts error:', error);
      const errorMessage =
        (error instanceof HttpErrorResponse && (error.error?.detail || error.error?.title)) ||
        this.translateService.instant('push-notifications.toast.user-counts-failed');
      this.toastService.showError(errorMessage);
    } finally {
      this.loadingUserCounts.set(false);
    }
  }

  backToStage1(): void {
    // Reset language states, counts, results, and notification content (keep targets intact)
    this.locales.forEach((locale) => {
      locale.enabled = false;
      this.form.get(this.getTitleControlName(locale.code))?.reset();
      this.form.get(this.getBodyControlName(locale.code))?.reset();
    });

    this.userCounts.set({});
    this.pushTokenCounts.set({});
    this.siteCountsByLanguage.set({});
    this.lastResult.set(null);
    this.currentStage.set(1);

    // Reset validation states without affecting values
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  toggleLanguage(locale: Locale): void {
    const localeItem = this.locales.find((l) => l.code === locale);
    if (!localeItem) return;

    localeItem.enabled = !localeItem.enabled;
    this.updateLocaleValidators(locale, localeItem.enabled);
    this.cdr.markForCheck();
  }

  getUserCount(locale: Locale): number {
    return this.userCounts()[locale.toLowerCase()] || 0;
  }

  getPushTokenCount(locale: Locale): number {
    return this.pushTokenCounts()[locale.toLowerCase()] || 0;
  }

  // Per-language site distribution chips (site locale + distinct user count), sorted by count desc.
  getSiteChips(locale: Locale): { site: string; count: number }[] {
    const sites = this.siteCountsByLanguage()[locale.toLowerCase()] ?? {};
    return Object.entries(sites)
      .map(([site, count]) => ({ site, count }))
      .sort((a, b) => b.count - a.count);
  }

  private updateLocaleValidators(locale: Locale, enabled: boolean): void {
    const titleControl = this.form.get(this.getTitleControlName(locale));
    const bodyControl = this.form.get(this.getBodyControlName(locale));

    if (titleControl && bodyControl) {
      if (enabled) {
        titleControl.setValidators([Validators.required, Validators.maxLength(100)]);
        bodyControl.setValidators([Validators.required, Validators.maxLength(500)]);
      } else {
        titleControl.setValidators([Validators.maxLength(100)]);
        bodyControl.setValidators([Validators.maxLength(500)]);
      }
      titleControl.updateValueAndValidity();
      bodyControl.updateValueAndValidity();
    }
  }

  async sendNotification(): Promise<void> {
    // Mark all fields as touched to trigger validation display
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.toastService.showError(this.translateService.instant('push-notifications.form.invalid'));
      return;
    }

    // Check if at least one language is enabled
    const hasEnabledLanguage = this.locales.some((l) => l.enabled);
    if (!hasEnabledLanguage) {
      this.toastService.showError(this.translateService.instant('push-notifications.validation.no-languages-enabled'));
      return;
    }

    this.sending.set(true);
    this.lastResult.set(null);

    try {
      const formValue = this.form.getRawValue();
      const targets: PushNotificationTarget[] = formValue.targets.map((target) => {
        const pushTarget: PushNotificationTarget = {
          type: target.type,
        };

        switch (target.type) {
          case PushNotificationTargetType.Reservation:
            pushTarget.reservationNumber = target.reservationNumber;
            break;
          case PushNotificationTargetType.Water:
            pushTarget.waterId = target.waterId ?? undefined;
            break;
          case PushNotificationTargetType.ArrivalDate:
            pushTarget.arrivalDateFrom = target.arrivalDateFrom ? this.formatDate(target.arrivalDateFrom) : undefined;
            pushTarget.arrivalDateTo = target.arrivalDateTo ? this.formatDate(target.arrivalDateTo) : undefined;
            break;
          case PushNotificationTargetType.User:
            pushTarget.userEmail = target.userEmail;
            break;
          case PushNotificationTargetType.Global:
            // No fields
            break;
          default:
            assertUnreachable(target.type);
        }

        return pushTarget;
      });

      // Only send enabled languages; for disabled languages, send empty strings
      const request: SendPushNotificationRequest = {
        titleNl: this.locales.find((l) => l.code === 'Nl')?.enabled ? formValue.titleNl : '',
        titleEn: this.locales.find((l) => l.code === 'En')?.enabled ? formValue.titleEn : '',
        titleDe: this.locales.find((l) => l.code === 'De')?.enabled ? formValue.titleDe : '',
        titleIt: this.locales.find((l) => l.code === 'It')?.enabled ? formValue.titleIt : '',
        titleFr: this.locales.find((l) => l.code === 'Fr')?.enabled ? formValue.titleFr : '',
        bodyNl: this.locales.find((l) => l.code === 'Nl')?.enabled ? formValue.bodyNl : '',
        bodyEn: this.locales.find((l) => l.code === 'En')?.enabled ? formValue.bodyEn : '',
        bodyDe: this.locales.find((l) => l.code === 'De')?.enabled ? formValue.bodyDe : '',
        bodyIt: this.locales.find((l) => l.code === 'It')?.enabled ? formValue.bodyIt : '',
        bodyFr: this.locales.find((l) => l.code === 'Fr')?.enabled ? formValue.bodyFr : '',
        targets,
      };

      const response = await firstValueFrom(this.pushNotificationsService.sendNotification(request));
      const resultMessage = this.translateService.instant('push-notifications.result.success', {
        users: response.usersTargeted,
        tokens: response.tokensTargeted,
        success: response.successCount,
        failures: response.failureCount,
      });

      this.lastResult.set({ success: true, message: resultMessage });
      await this.toastService.showSuccess(this.translateService.instant('push-notifications.toast.send-success'));

      // Keep user on stage 2 to see detailed results
      // They can manually go back to stage 1 if they want to send another notification
    } catch (error: unknown) {
      console.error('Send notification error:', error);
      const errorMessage =
        (error instanceof HttpErrorResponse && (error.error?.detail || error.error?.title)) ||
        this.translateService.instant('push-notifications.toast.send-failed');
      this.lastResult.set({ success: false, message: errorMessage });
      this.toastService.showError(errorMessage);
    } finally {
      this.sending.set(false);
    }
  }

  private validateTargets(): string | null {
    const targets = this.form.controls.targets.getRawValue();

    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];

      switch (target.type) {
        case PushNotificationTargetType.Reservation:
          if (!target.reservationNumber || target.reservationNumber.trim() === '') {
            return this.translateService.instant('push-notifications.validation.reservation-required', {
              index: i + 1,
            });
          }
          break;
        case PushNotificationTargetType.Water:
          if (!target.waterId) {
            return this.translateService.instant('push-notifications.validation.water-required', { index: i + 1 });
          }
          break;
        case PushNotificationTargetType.ArrivalDate:
          if (!target.arrivalDateFrom) {
            return this.translateService.instant('push-notifications.validation.date-required', { index: i + 1 });
          }
          if (target.arrivalDateTo && target.arrivalDateTo < target.arrivalDateFrom) {
            return this.translateService.instant('push-notifications.validation.date-range-invalid', { index: i + 1 });
          }
          break;
        case PushNotificationTargetType.User: {
          if (!target.userEmail || target.userEmail.trim() === '') {
            return this.translateService.instant('push-notifications.validation.email-required', { index: i + 1 });
          }
          // Basic email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(target.userEmail)) {
            return this.translateService.instant('push-notifications.validation.email-invalid', { index: i + 1 });
          }
          break;
        }
      }
    }

    return null;
  }

  // Callers guard the date, so toDateOnly always returns a string here.
  private formatDate(date: Date): string {
    return toDateOnly(date) ?? '';
  }

  getTargetTypeLabel(type: PushNotificationTargetType): string {
    return this.translateService.instant(`push-notifications.target-type.${type.toLowerCase()}`);
  }

  getTitleControlName(locale: Locale): string {
    return `title${locale}`;
  }

  getBodyControlName(locale: Locale): string {
    return `body${locale}`;
  }

  isLocaleEnabled(locale: Locale): boolean {
    return this.locales.find((l) => l.code === locale)?.enabled || false;
  }

  readonly translating = signal(false);

  async translateMissing(): Promise<void> {
    const sourceTitle = this.form.get(this.getTitleControlName(SOURCE_LOCALE))?.value || '';
    const sourceBody = this.form.get(this.getBodyControlName(SOURCE_LOCALE))?.value || '';

    // Skip if source NL doesn't have both title and body
    if (!sourceTitle.trim() || !sourceBody.trim()) {
      this.toastService.showError(this.translateService.instant('push-notifications.form.translate-no-source'));
      return;
    }

    // Determine which target languages need translation
    const neededLanguages = TARGET_LOCALES.filter((targetLang) => {
      const targetTitle = this.form.get(this.getTitleControlName(targetLang))?.value || '';
      const targetBody = this.form.get(this.getBodyControlName(targetLang))?.value || '';
      // Need translation if either title or body is empty
      return !targetTitle.trim() || !targetBody.trim();
    });

    // Check if there's anything to translate
    if (neededLanguages.length === 0) {
      this.toastService.showError(this.translateService.instant('push-notifications.form.translate-no-missing'));
      return;
    }

    this.translating.set(true);

    try {
      const response = await firstValueFrom(
        this.pushNotificationsService.translateNotification({
          from: SOURCE_LOCALE.toLowerCase(),
          to: neededLanguages.map((l) => l.toLowerCase()),
          title: sourceTitle,
          body: sourceBody,
        }),
      );

      if (response.success && response.translations) {
        // Update form with translated values
        Object.entries(response.translations).forEach(([lang, fields]) => {
          const targetLocale = this.capitalizeFirst(lang) as Locale;

          // Only update title if target field is empty
          const titleControl = this.form.get(this.getTitleControlName(targetLocale));
          const currentTitle = titleControl?.value || '';
          if (!currentTitle.trim() && titleControl) {
            titleControl.setValue(fields.title);
          }

          // Only update body if target field is empty
          const bodyControl = this.form.get(this.getBodyControlName(targetLocale));
          const currentBody = bodyControl?.value || '';
          if (!currentBody.trim() && bodyControl) {
            bodyControl.setValue(fields.body);
          }
        });

        this.toastService.showSuccess(this.translateService.instant('push-notifications.form.translate-success'));
        this.cdr.detectChanges();
      } else {
        this.toastService.showError(
          response.message || this.translateService.instant('push-notifications.form.translate-failed'),
        );
      }
    } catch (error: unknown) {
      console.error('Translation error while translating notification', error);
      this.toastService.showError(this.translateService.instant('push-notifications.form.translate-failed'));
    } finally {
      this.translating.set(false);
    }
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
